import { useMutation } from '@tanstack/vue-query'
import { markRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import { requestSsoRedirect } from '@/features/sso/api/sso'
import type { ApiError } from '@/lib/api/errors'

export interface SsoTarget {
  tab: Window | null
}

/**
 * The tab is wrapped in a raw holder because Vue would otherwise probe the Window for reactive
 * flags, which throws once the tab has navigated to a cross-origin page.
 */
export function createSsoTarget(tab: Window | null): SsoTarget {
  return markRaw({ tab })
}

/**
 * Pass a target built from a pre-opened tab to load the SSO URL there. The tab must be
 * opened synchronously in the click handler, otherwise browsers block it as a popup.
 */
export function useSsoRedirect() {
  const { t } = useI18n()

  return useMutation({
    mutationFn: (_target?: SsoTarget) => requestSsoRedirect(),
    onSuccess: (redirectUrl, target) => {
      if (target?.tab) target.tab.location.href = redirectUrl
      else window.location.assign(redirectUrl)
    },
    onError: (error: unknown, target) => {
      target?.tab?.close()
      toast.error((error as ApiError).message || t('sso.redirectFailed'))
    },
  })
}
